import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Box, Icon } from '@nimbus-ds/components';
import { IdCardIcon } from '@nimbus-ds/icons';
import { useQueryClient } from '@tanstack/react-query';
import AppLayout from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import { ErrorMessage, FormattedMultilineText } from 'commons/components';
import { useBankAccount, useIdentityValidation, useToast } from 'commons/hooks';
import { GetIdValidationResponse } from 'commons/services';
import {
  formatCuit,
  getLegalEntityType,
  useTranslationWithPrefix,
} from 'commons/utils';
import { hasKycErrors } from 'commons/utils/getKycError';
import { DocumentsContext } from '../../DocumentsContext';
import StatuteDocument from '../../StatuteDocument';
import FooterButtons from '../FooterButtons';
import { useIdValidationRoutes, useKycInfoMutation } from '../hooks';
import JuridicalValidationSkeleton from './JuridicalValidationSkeleton';

function JuridicalValidation(): JSX.Element {
  const { t } = useTranslationWithPrefix('identityValidation');
  const documents = useContext(DocumentsContext);
  const identityValidation = useIdentityValidation();
  const bankAccount = useBankAccount();
  const history = useHistory();
  const { goToNextRoute, getNextRoute } = useIdValidationRoutes();
  const { showToastError } = useToast();
  const queryClient = useQueryClient();
  const [statuteUploadCompleted, setStatuteUploadCompleted] = useState(false);

  useEffect(() => {
    if (documents.statute) {
      setStatuteUploadCompleted(true);
    }
  }, [documents.statute]);

  // When the user reached this screen to submit additional documentation for a
  // `legal_representative_not_found_in_company` error, the flow does not go
  // through ProofOfLife afterwards, so nothing would flip the alert to
  // "en análisis". So the KYC status reflects the pending review right after
  // the backend submit succeeds, without waiting for the dLocal webhook.
  const { hasLegalRepresentativeNotFoundInCompanyError } = hasKycErrors(
    identityValidation.data?.kycDocumentErrors ?? [],
  );

  // Forcing `pending_review` only makes sense when this submit actually ends the
  // flow at config. If the resolved next route is anything else (e.g. proof of
  // life, because another error is still pending), we must not flip the status
  // nor clear the errors — keep the optimistic update coupled to landing on config.
  const willFinishAtConfig =
    hasLegalRepresentativeNotFoundInCompanyError &&
    getNextRoute() === ROUTES.CONFIG;

  const setKycStatusToPendingReview = useCallback(async () => {
    const queryKey = ['identityValidation'];
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData<GetIdValidationResponse>(queryKey, (old) => ({
      ...old,
      status: 'pending_review',
      updatedAt: new Date().toISOString(),
      kycDocumentErrors: [],
    }));
    queryClient.setQueryDefaults(queryKey, { staleTime: Infinity });
  }, [queryClient]);

  const { mutate, isLoading } = useKycInfoMutation({
    onSuccess: async () => {
      try {
        if (willFinishAtConfig) {
          track('pago-nube-prueba-de-vida-analisis', {
            legal_entity_type: getLegalEntityType(
              bankAccount.data?.fiscalDocument,
            ),
          });
          await setKycStatusToPendingReview();
        }
      } finally {
        goToNextRoute();
      }
    },
  });

  const handleSubmit = () => {
    track('pago-nube-prueba-de-vida-estatuto-continue-click', {
      has_uploaded_document: statuteUploadCompleted,
      legal_entity_type: getLegalEntityType(bankAccount.data?.fiscalDocument),
    });
    if (statuteUploadCompleted) {
      mutate(1); // numberOfOwners = 1
    } else {
      showToastError(t('errors.uploadOneFile'));
    }
  };

  if (identityValidation.isLoading) {
    return <JuridicalValidationSkeleton />;
  }

  if (identityValidation.isError) {
    return (
      <AppLayout title={t('title')} subtitle={t('subtitle')}>
        <Box
          display="flex"
          alignItems="stretch"
          justifyContent="flex-end"
          flexDirection="column"
        >
          <ErrorMessage />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('title')}
      footer={
        <FooterButtons
          onCancelClick={history.goBack}
          onSubmitClick={handleSubmit}
          isLoading={isLoading}
        />
      }
    >
      <Box display="flex" flexDirection="column" gap="2">
        {bankAccount.data?.fiscalDocument ? (
          <Box display="flex" flexDirection="row" gap="2">
            <Icon source={<IdCardIcon />} color="primary-interactive" />
            <FormattedMultilineText formattedFontWeight="bold">
              {t('cuitOrCuil', {
                document: formatCuit(bankAccount.data?.fiscalDocument),
              })}
            </FormattedMultilineText>
          </Box>
        ) : null}
        <StatuteDocument
          verifyUploadedDocuments={setStatuteUploadCompleted}
          statuteThumbnailFile={documents.statute}
        />
      </Box>
    </AppLayout>
  );
}

export default JuridicalValidation;
